import type { FormInstance } from '@arco-design/web-react/es/Form';

import { readInitCommandFieldsFromForm } from '@/delivery-type-plugin/capabilities/command-args-normal';
import { readRegistryPayloadFromForm } from '@/delivery-type-plugin/capabilities/image';
import { readMaasFormValues } from '@/delivery-type-plugin/capabilities/maas-init/utils/formValues';
import { MODEL_CATEGORY_FORM_FIELD, normalizeModelCategory } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelCategory';
import type { MaasModel, ModelInstanceFormValues } from '@/delivery-type-plugin/capabilities/maas-model-storage/modelInstanceTypes';
import { getDataVolumeStorageGi } from '@/delivery-type-plugin/capabilities/maas-model-storage/storageVolume';
import {
  isExistingDataVolume,
  resolvePrimaryDataVolumeFromValues,
} from '@/delivery-type-plugin/capabilities/maas-node-groups/masterGroupForm';
import { parseNodesField } from '@/delivery-type-plugin/capabilities/maas-node-groups/nodeFields';
import { TASK_TEMPLATE_FIELD } from '@/delivery-type-plugin/capabilities/maas-task-template/types';
import { SKU_RESOURCE_MODULES_FIELD } from '@/delivery-type-plugin/platform/scene-layout/types';

import { getModelSizeGiFromModel, getTotalModelSourcesSizeGi, normalizeModelSources } from './modelSources';
import { parseStorageQuantityToGi } from './quantity';

/** 从 model.sources 合计 size（Gi），供 PVC 容量筛选 */
export function getModelSizeGi(model?: MaasModel): number | undefined {
  return getModelSizeGiFromModel(model);
}

/** PD 开关未回读时，可从 prefill/decode 节点字段推断（多步提交常见） */
export function resolveModelInstancePdSeparation(
  values: Pick<
    ModelInstanceFormValues,
    '_inferencePdSeparation' | 'prefillMasterNodes' | 'prefillWorkerNodes' | 'decodeMasterNodes' | 'decodeWorkerNodes'
  >,
): boolean {
  if (values._inferencePdSeparation === true) return true;
  /** 用户显式关闭 PD 时不再根据残留的 prefill/decode 字段推断，避免提交仍走 prefillDecode */
  if (values._inferencePdSeparation === false) return false;
  return (
    parseNodesField(values.prefillMasterNodes).length > 0 ||
    parseNodesField(values.prefillWorkerNodes).length > 0 ||
    parseNodesField(values.decodeMasterNodes).length > 0 ||
    parseNodesField(values.decodeWorkerNodes).length > 0
  );
}

const MODEL_INSTANCE_PD_FORM_FIELD_KEYS = [
  '_inferencePdSeparation',
  'prefillReplicas',
  'prefillTaskTemplate',
  'prefillRunMode',
  'prefillMasterNodes',
  'prefillWorkerNodes',
  'decodeReplicas',
  'decodeTaskTemplate',
  'decodeRunMode',
  'decodeMasterNodes',
  'decodeWorkerNodes',
] as const satisfies readonly (keyof ModelInstanceFormValues)[];

function readFormFieldFallback(form: FormInstance, values: ModelInstanceFormValues, key: string): unknown {
  const fromForm = form.getFieldValue(key);
  if (fromForm !== undefined) return fromForm;
  return (values as Record<string, unknown>)[key];
}

/** 多步表单确认/提交：显式回读 PD 开关与 prefill/decode 节点 JSON（避免仅依赖 getFields 聚合） */
export function readModelInstancePdFormFields(
  form: FormInstance,
  values: ModelInstanceFormValues,
): Pick<ModelInstanceFormValues, (typeof MODEL_INSTANCE_PD_FORM_FIELD_KEYS)[number]> {
  const prefillMasterNodes = readFormFieldFallback(form, values, 'prefillMasterNodes') as string | undefined;
  const prefillWorkerNodes = readFormFieldFallback(form, values, 'prefillWorkerNodes') as string | undefined;
  const decodeMasterNodes = readFormFieldFallback(form, values, 'decodeMasterNodes') as string | undefined;
  const decodeWorkerNodes = readFormFieldFallback(form, values, 'decodeWorkerNodes') as string | undefined;
  const pdFlag = readFormFieldFallback(form, values, '_inferencePdSeparation');
  const mergedForInfer = {
    ...values,
    prefillMasterNodes,
    prefillWorkerNodes,
    decodeMasterNodes,
    decodeWorkerNodes,
    _inferencePdSeparation: pdFlag === true ? true : values._inferencePdSeparation,
  };
  const inferredPd = resolveModelInstancePdSeparation(mergedForInfer);

  return {
    _inferencePdSeparation: pdFlag === true || (pdFlag !== false && inferredPd),
    prefillReplicas: readFormFieldFallback(form, values, 'prefillReplicas') as ModelInstanceFormValues['prefillReplicas'],
    prefillTaskTemplate: readFormFieldFallback(form, values, 'prefillTaskTemplate') as ModelInstanceFormValues['prefillTaskTemplate'],
    prefillRunMode: readFormFieldFallback(form, values, 'prefillRunMode') as ModelInstanceFormValues['prefillRunMode'],
    prefillMasterNodes,
    prefillWorkerNodes,
    decodeReplicas: readFormFieldFallback(form, values, 'decodeReplicas') as ModelInstanceFormValues['decodeReplicas'],
    decodeTaskTemplate: readFormFieldFallback(form, values, 'decodeTaskTemplate') as ModelInstanceFormValues['decodeTaskTemplate'],
    decodeRunMode: readFormFieldFallback(form, values, 'decodeRunMode') as ModelInstanceFormValues['decodeRunMode'],
    decodeMasterNodes,
    decodeWorkerNodes,
  };
}

/** 读取表单 UI 字段（不组装组 spec，供确认页与提交前校验使用） */
export function readModelInstanceFormValues(form: FormInstance): ModelInstanceFormValues {
  const values = readMaasFormValues(form) as ModelInstanceFormValues;
  const mountPath = form.getFieldValue('model.mountPath') as string | undefined;
  const modelCategory = normalizeModelCategory(form.getFieldValue(MODEL_CATEGORY_FORM_FIELD) ?? values._modelCategory);
  const sourcesRaw = form.getFieldValue('model.sources') ?? values.model?.sources;
  const sources = Array.isArray(sourcesRaw) ? (sourcesRaw as MaasModel['sources']) : normalizeModelSources(values.model);
  const model: MaasModel = {
    mountPath: String(mountPath || values.model?.mountPath || '/models').trim() || '/models',
    sources,
    ...(modelCategory.length ? { types: modelCategory } : {}),
  };
  const modelStorageMode = form.getFieldValue('_modelStorageMode') ?? values._modelStorageMode;
  const initCommandFields = readInitCommandFieldsFromForm(form);
  const taskTemplate = (form.getFieldValue(TASK_TEMPLATE_FIELD) ?? values.taskTemplate) as ModelInstanceFormValues['taskTemplate'];
  const skuResourceModules = (form.getFieldValue(SKU_RESOURCE_MODULES_FIELD) ??
    values.skuResourceModules) as ModelInstanceFormValues['skuResourceModules'];
  const inferenceFrameworkImage = form.getFieldValue('inferenceFrameworkImage') ?? values.inferenceFrameworkImage;
  const registry = readRegistryPayloadFromForm(form);
  const pdFields = readModelInstancePdFormFields(form, values);
  return {
    ...values,
    ...pdFields,
    ...(registry ? { registry } : {}),
    model,
    ...(initCommandFields ? { initCommand: initCommandFields as ModelInstanceFormValues['initCommand'] } : {}),
    ...(taskTemplate != null && taskTemplate !== '' ? { taskTemplate } : {}),
    ...(skuResourceModules != null ? { skuResourceModules } : {}),
    ...(inferenceFrameworkImage != null && inferenceFrameworkImage !== '' ? { inferenceFrameworkImage } : {}),
    ...(modelCategory.length ? { _modelCategory: modelCategory } : {}),
    ...(modelStorageMode === 'new' || modelStorageMode === 'existing' ? { _modelStorageMode: modelStorageMode } : {}),
  };
}

export { getModelSourceType } from './modelSources';

export function getModelSizeStorageMismatchErrorKey(values: ModelInstanceFormValues): string | undefined {
  const modelSizeGi = getTotalModelSourcesSizeGi(normalizeModelSources(values.model));
  if (modelSizeGi === undefined) return undefined;

  const dataVolume = resolvePrimaryDataVolumeFromValues(values);
  if (!dataVolume) return undefined;

  const storageGi = getDataVolumeStorageGi(dataVolume);
  if (storageGi === undefined) return undefined;

  if (modelSizeGi > storageGi) {
    return 'data-center:maas.model.size.exceeds.pvc';
  }
  return undefined;
}
